import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Wallet,
  ClipboardList,
  Star,
  Award,
  BadgeCheck,
  Clock,
  TrendingUp,
  ArrowRight,
  LogOut,
  User,
  ChevronDown,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { formatRupiah } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { StarRating } from "../../components/StarRating";
import { ProfileAvatar } from "../../components/ProfilePhoto";
import UsageTour, { TourReplayButton } from "../../components/UsageTour";
import BannerCarousel from "../../components/BannerCarousel";
import { cacheGet, cacheSet, cacheKey } from "../../lib/cache";

const WORKER_TOUR = [
  {
    title: "Selamat datang, Partner! 🔧",
    text: "Panduan 30 detik: cara tawarkan jasa, tangani pesanan, cairkan uang, dan naik tier biar makin banyak yang masuk kantong.",
  },
  {
    selector: "worker-quick-services",
    title: "1. Tawarkan jasamu",
    text: "Tambah layanan dari katalog, atur harga sendiri, dan pasang 1 foto hasil kerja biar dilirik pelanggan.",
    to: "/worker/services",
    toLabel: "Kelola Jasa",
    thenKey: "tour-worker-services",
  },
  {
    selector: "worker-quick-bookings",
    title: "2. Tangani pesanan masuk",
    text: "Alur kerja: TERIMA → MULAI (wajib foto sebelum) → SELESAI (wajib foto sesudah + catatan). Ketemu masalah tambahan di lokasi? Ajukan penyesuaian harga dari detail pesanan — jalan hanya bila pelanggan setuju. 30% dana cair saat pelanggan konfirmasi, 70% otomatis 7 hari kemudian.",
    to: "/worker/bookings",
    toLabel: "Lihat Pesanan",
    thenKey: "tour-worker-bookings",
  },
  {
    selector: "worker-quick-finance",
    title: "3. Keuangan & penarikan",
    text: "Pantau yang sudah cair, yang masih ditahan aman, grafik 6 bulan, dan tarik ke rekening (min Rp50rb). Klaim promo potongan admin juga di sini.",
    to: "/worker/finance",
    toLabel: "Buka Keuangan",
  },
  {
    selector: "worker-kyc-status",
    title: "4. Verifikasi dulu biar dipercaya",
    text: "Baca SNK lalu upload KTP + selfie + SKCK di Profil. Tanpa status Terverifikasi, pesananmu dibatasi. Badge ✓ menaikkan kepercayaan pelanggan.",
    to: "/worker/kyc-terms",
    toLabel: "Baca SNK & Verifikasi",
  },
  {
    title: "5. Naik tier, potongan turun 🏆",
    text: "LITE potongan admin 20% (kamu terima 80%) → ELITE 15% (terima 85%) → PRO 12% (terima 88%). Selesaikan job, jaga rating & komplain pelanggan rendah — tier naik OTOMATIS oleh sistem tiap menit. Semangat!",
  },
];
import NotificationBell from "../../components/NotificationBell";

export default function WorkerHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const tier = user?.profile?.tier || "LITE";
  const kyc = user?.profile?.kyc_status || "PENDING";
  const isSuspended = user?.profile?.is_suspended || false;
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState("");
  const [tierProgress, setTierProgress] = useState(null);

  const displayName = user?.profile?.display_name || "Pekerja";
  const initials = (displayName || "P")[0].toUpperCase();

  const doLogout = async () => {
    setProfileOpen(false);
    await logout();
    navigate("/login");
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchStats = async () => {
    setStatsError("");
    // Stale-while-revalidate: cache tampil instan, network refresh di belakang.
    const sKey = cacheKey(user?.id, "worker-stats");
    const tKey = cacheKey(user?.id, "tier-progress");
    const cachedS = cacheGet(sKey);
    const cachedT = cacheGet(tKey);
    if (cachedS.data) setStats(cachedS.data);
    if (cachedT.data) setTierProgress(cachedT.data);
    try {
      const [sRes, tRes] = await Promise.all([
        api.get("/api/bookings/stats"),
        api.get("/api/worker/tier-progress").catch(() => ({ data: null })),
      ]);
      setStats(sRes.data);
      cacheSet(sKey, sRes.data);
      if (tRes.data) {
        setTierProgress(tRes.data);
        cacheSet(tKey, tRes.data);
      }
    } catch (err) {
      if (!cachedS.data) {
        setStatsError(err.response?.data?.detail || err.message || "Gagal memuat statistik");
      }
      // Bila ada cache: diamkan (data lama tetap tampil), error tak block UI.
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const tierConfig = {
    LITE: { label: "LITE", color: "bg-brand-100 text-brand-700", commission: "20%", keep: "80%" },
    ELITE: { label: "ELITE", color: "bg-brand-600 text-white", commission: "15%", keep: "85%" },
    PRO: { label: "PRO", color: "bg-success-500 text-white", commission: "12%", keep: "88%" },
  };
  const tierInfo = tierConfig[tier] || tierConfig.LITE;

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      <UsageTour tourKey="tour-worker-v1" steps={WORKER_TOUR} />
      <div className="container space-y-6">
        {/* Header */}
        <div className="relative z-20 rounded-3xl bg-brand-700 text-white p-6 md:p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-brand-100">Selamat datang,</p>
              <h1 className="text-2xl md:text-3xl">{displayName}</h1>
            </div>
            <div className="flex items-center gap-3">
              <span className={`chip ${tierInfo.color}`} data-testid="worker-tier">
                <Award className="h-4 w-4" /> Tier {tierInfo.label}
              </span>
              <NotificationBell tone="dark" />
              <TourReplayButton tourKey="tour-worker-v1" />
              {/* Profile dropdown */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="h-11 w-11 rounded-full overflow-hidden bg-white/15 grid place-items-center text-sm font-bold text-white hover:bg-white/25 transition"
                  data-testid="worker-home-profile-btn"
                >
                  <ProfileAvatar
                    userId={user?.id}
                    photoUrl={user?.profile?.profile_photo_url}
                    name={displayName}
                    className="h-11 w-11 rounded-full"
                    fallbackClassName="h-11 w-11 rounded-full bg-white/15 grid place-items-center text-sm font-bold text-white"
                  />
                </button>
                {profileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-lg border border-brand-100 py-2 animate-fade-up z-50">
                    <div className="px-4 py-2 border-b border-brand-50">
                      <p className="text-sm font-bold text-brand-800">{displayName}</p>
                      <p className="text-xs text-brand-400">Pekerja • Tier {tierInfo.label}</p>
                    </div>
                    <Link
                      to="/worker/profile"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-700 hover:bg-brand-50 transition"
                      data-testid="worker-home-profile-link"
                    >
                      <User className="h-4 w-4" /> Profil Saya
                    </Link>
                    <button
                      onClick={doLogout}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition w-full text-left"
                      data-testid="worker-home-logout-btn"
                    >
                      <LogOut className="h-4 w-4" /> Keluar
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          {isSuspended && (
            <div className="mt-4 rounded-2xl bg-red-500/20 px-4 py-3 text-sm font-medium">
              Akun Anda sedang ditinjau. Beberapa fitur mungkin terbatas.
            </div>
          )}
        </div>

        {/* Profile completion banner */}
        {!user?.profile?.display_name && (
          <Link to="/worker/profile" className="card p-4 border-amber-200 bg-amber-50 flex items-center gap-3 hover:border-amber-300 transition" data-testid="worker-profile-completion-banner">
            <div className="h-10 w-10 rounded-xl bg-amber-100 grid place-items-center flex-shrink-0">
              <BadgeCheck className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-brand-800">Lengkapi profil Anda</p>
              <p className="text-sm text-brand-500">Tambahkan nama tampilan untuk memulai.</p>
            </div>
            <ArrowRight className="h-5 w-5 text-amber-500 flex-shrink-0" />
          </Link>
        )}

        {/* KYC Banner */}
        {kyc !== "APPROVED" && (
          <Link to="/worker/kyc-terms" className="card p-5 border-amber-200 bg-amber-50 block hover:border-amber-300 transition" data-testid="worker-kyc-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BadgeCheck className="h-7 w-7 text-amber-500" />
                <div>
                  <p className="font-bold text-brand-800">Verifikasi Identitas (KYC)</p>
                  <p className="text-sm text-brand-500">Lengkapi verifikasi untuk mulai menerima pesanan.</p>
                </div>
              </div>
              <span className="chip bg-amber-100 text-amber-700" data-testid="worker-kyc-status">
                {kyc === "APPROVED" ? "Terverifikasi" : kyc === "SKCK_PENDING" ? "SKCK Belum" : "Belum"}
              </span>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="chip bg-brand-50 text-brand-500">
                <Clock className="h-4 w-4" /> Baca SNK lalu lengkapi KTP, Selfie & SKCK
              </span>
              <ArrowRight className="h-4 w-4 text-amber-500" />
            </div>
          </Link>
        )}

        {/* Banner program (P3) */}
        <BannerCarousel audience="worker" />

        {/* Stats */}
        {statsError && (
          <div className="card p-4 border-red-200 bg-red-50 flex items-center gap-3" data-testid="worker-stats-error">
            <div className="flex-1">
              <p className="font-bold text-red-700 text-sm">Statistik tidak tampil</p>
              <p className="text-sm text-red-500">{statsError}</p>
            </div>
            <button onClick={fetchStats} className="btn-primary text-sm !py-2">
              Muat ulang
            </button>
          </div>
        )}
        <div className="grid sm:grid-cols-3 gap-4">
          <StatCard
            icon={ClipboardList}
            label="Pesanan Aktif"
            value={stats ? String(stats.active + stats.pending) : "—"}
            note={stats ? `${stats.pending} menunggu • ${stats.active} berlangsung` : "Memuat..."}
          />
          <StatCard
            icon={TrendingUp}
            label="Selesai"
            value={stats ? String(stats.completed) : "—"}
            note={stats ? `${stats.services_count} jasa ditawarkan` : "Memuat..."}
          />
          <div className="card p-5">
            <div className="flex items-center gap-2 text-brand-500">
              <Star className="h-5 w-5" /> <span className="font-semibold">Rating</span>
            </div>
            {stats && stats.average_rating ? (
              <div className="mt-2">
                <StarRating rating={stats.average_rating} count={stats.review_count} size="lg" />
              </div>
            ) : (
              <>
                <p className="text-3xl font-extrabold text-brand-800 mt-2">—</p>
                <p className="text-sm text-brand-400 mt-1">Belum ada rating</p>
              </>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-3 gap-3">
          <Link to="/worker/bookings" className="card p-5 flex items-center gap-4 hover:border-brand-200 transition" data-testid="worker-quick-bookings">
            <div className="h-12 w-12 rounded-xl bg-brand-50 grid place-items-center">
              <ClipboardList className="h-6 w-6 text-brand-600" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-brand-800">Pesanan Masuk</p>
              <p className="text-sm text-brand-400">Lihat & kelola pesanan baru</p>
            </div>
            <ArrowRight className="h-5 w-5 text-brand-300" />
          </Link>
          <Link to="/worker/services" className="card p-5 flex items-center gap-4 hover:border-brand-200 transition" data-testid="worker-quick-services">
            <div className="h-12 w-12 rounded-xl bg-success-50 grid place-items-center">
              <TrendingUp className="h-6 w-6 text-success-500" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-brand-800">Jasa Saya</p>
              <p className="text-sm text-brand-400">Kelola layanan yang ditawarkan</p>
            </div>
            <ArrowRight className="h-5 w-5 text-brand-300" />
          </Link>
          <Link to="/worker/finance" className="card p-5 flex items-center gap-4 hover:border-brand-200 transition" data-testid="worker-quick-finance">
            <div className="h-12 w-12 rounded-xl bg-amber-50 grid place-items-center">
              <Wallet className="h-6 w-6 text-amber-500" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-brand-800">Keuangan</p>
              <p className="text-sm text-brand-400">Pemasukkan & grafik 6 bulan</p>
            </div>
            <ArrowRight className="h-5 w-5 text-brand-300" />
          </Link>
        </div>

        {/* Naik Level */}
        <TierProgressCard progress={tierProgress} tierConfig={tierConfig} />

        {/* Commission Info */}
        <div className="card p-6">
          <h2 className="text-brand-800">Potongan Admin</h2>
          <p className="text-brand-400 mt-1">Potongan admin per tier — sisanya masuk kantongmu</p>          <div className="mt-4 grid grid-cols-3 gap-3">
            {Object.entries(tierConfig).map(([key, t]) => (
              <div
                key={key}
                className={`rounded-2xl border-2 p-4 text-center ${
                  key === tier ? "border-brand-600 bg-brand-50" : "border-brand-50"
                }`}
              >
                <span className={`chip ${t.color} mb-2`}>{t.label}</span>
                <p className="text-2xl font-extrabold text-brand-800 mt-2">{t.commission}</p>
                <p className="text-xs text-brand-400">potongan admin</p>
                <p className="text-sm font-bold text-success-600 mt-1">kamu terima {t.keep}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, note }) {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 text-brand-500">
        <Icon className="h-5 w-5" /> <span className="font-semibold">{label}</span>
      </div>
      <p className="text-3xl font-extrabold text-brand-800 mt-2">{value}</p>
      <p className="text-sm text-brand-400 mt-1">{note}</p>
    </div>
  );
}

function TierProgressCard({ progress, tierConfig }) {
  if (!progress) return null;
  const { current_tier, next_tier, requirements, all_met } = progress;

  if (!next_tier) {
    return (
      <div className="rounded-3xl bg-brand-700 text-white p-5 md:p-6" data-testid="tier-max">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5" />
          <p className="font-bold">Tier {current_tier} — level tertinggi! 🎉</p>
        </div>
        <p className="text-sm text-brand-100 mt-1">
          Pertahankan kualitas kerja. Potongan admin Anda {tierConfig?.[current_tier]?.commission || ""} — kamu terima bersih {tierConfig?.[current_tier]?.keep || ""}.
        </p>
      </div>
    );
  }

  const nextCommission = tierConfig?.[next_tier]?.commission;
  // Perspektif pekerja: bukan "komisi turun", tapi "yang masuk kantong naik".
  const nextKeep = nextCommission
    ? (Math.round((100 - parseFloat(String(nextCommission).replace(",", "."))) * 10) / 10)
    : null;
  const doneCount = requirements.filter((r) => r.met).length;

  return (
    <div className="card p-5 md:p-6 border-brand-200" data-testid="tier-progress">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-brand-800 flex items-center gap-2">
            <Award className="h-5 w-5 text-amber-500" /> Naik ke {next_tier}
          </h2>
          <p className="text-sm text-brand-400 mt-0.5">
            {doneCount}/{requirements.length} syarat terpenuhi
            {nextKeep !== null ? ` • kamu terima bersih ${String(nextKeep).replace(".", ",")}%` : ""}
          </p>
        </div>
        {all_met && (
          <span className="chip bg-success-50 text-success-600 flex-shrink-0">
            <CheckCircle2 className="h-3.5 w-3.5" /> Lengkap!
          </span>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {requirements.map((r) => (
          <TierReqRow key={r.key} req={r} />
        ))}
      </div>

      {all_met && (
        <p className="text-xs text-brand-500 mt-4 bg-brand-50 rounded-xl px-3 py-2">
          Semua syarat terpenuhi. Tier naik otomatis oleh sistem (cek berkala tiap menit).
        </p>
      )}
    </div>
  );
}

function TierReqRow({ req }) {
  // progress 0..1 (untuk "<=" makin kecil makin penuh)
  const ratio =
    req.op === "<="
      ? req.current <= req.target
        ? 1
        : Math.max(0, req.target / Math.max(req.current, 0.001))
      : req.target > 0
      ? Math.min(1, req.current / req.target)
      : 1;
  const pct = Math.round(ratio * 100);
  return (
    <div data-testid={`tier-req-${req.key}`}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="flex items-center gap-1.5 font-semibold text-brand-700">
          {req.met ? (
            <CheckCircle2 className="h-4 w-4 text-success-500 flex-shrink-0" />
          ) : (
            <Circle className="h-4 w-4 text-brand-300 flex-shrink-0" />
          )}
          {req.label}
        </span>
        <span className={`font-extrabold flex-shrink-0 ${req.met ? "text-success-600" : "text-brand-800"}`}>
          {req.current}
          <span className="font-normal text-brand-400">
            /{req.target}
            {req.unit}
          </span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-brand-50 mt-1.5 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${req.met ? "bg-success-500" : "bg-brand-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
